/**
 * App-header launcher (owner: P1). Main's look: a 28px icon button with the
 * mascot outline at rest (like the neighbouring header icons), the filled
 * purple mascot on hover and the tap hop on click.
 * - Click toggles the panel and RESTORES the last conversation (never starts a new one).
 * - Open state: header hover wash + `aria-expanded`.
 * - While a reply runs with the panel closed: a 6px green dot on the icon's
 *   corner (no extra motion).
 * - Ctrl/⌘+J does the same from anywhere (handled by the shell).
 */
import React, { useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useConsultoriaChat } from '../context/ConsultoriaChatContext';
import OpsMascotIcon from '../components/mascot/OpsMascotIcon';
import { getOctoTokens, kbFocus, octoVisuallyHidden, useInputModality } from '../ui';

const isMac = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '');

/** Main's header action icon size (18) + 2, as in 9f4b3e8. */
const ICON_SIZE = 20;
/** Main's hover mascot colour. */
const MASCOT_HOVER = '#8A63FF';

export interface OctoplusLauncherProps {
  /** App header text colour (the header is dark in both themes). */
  headerText: string;
  headerHoverBg: string;
}

export const OctoplusLauncher: React.FC<OctoplusLauncherProps> = ({
  headerText,
  headerHoverBg,
}) => {
  const { isPanelOpen, togglePanel, assistantBusy } = useConsultoriaChat();
  const [tapKey, setTapKey] = useState(0);
  const showBusy = assistantBusy && !isPanelOpen;
  const shortcut = isMac() ? '⌘J' : 'Ctrl+J';
  // The app header is dark in both themes: dark-mode tokens for the ring/dot.
  const onHeader = getOctoTokens('dark').color;
  // Rings follow keyboard navigation only (the tracker is global; install it here too).
  useInputModality();

  return (
    <Tooltip
      title={isPanelOpen ? `Fechar Otto (${shortcut})` : `Abrir Otto (${shortcut})`}
      disableInteractive
    >
      <IconButton
        size="small"
        data-harness="launcher"
        aria-expanded={isPanelOpen}
        aria-keyshortcuts="Control+J Meta+J"
        onClick={() => {
          setTapKey((k) => k + 1);
          togglePanel();
        }}
        sx={{
          p: 0,
          m: 0,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '6px',
          position: 'relative',
          overflow: 'visible',
          color: headerText,
          bgcolor: isPanelOpen ? headerHoverBg : 'transparent',
          // Outline at rest, like the neighbouring icons; purple mascot on hover.
          '& .ops-mascot-hover': { display: 'none' },
          '&:hover': {
            backgroundColor: headerHoverBg,
            '& .ops-mascot-rest': { display: 'none' },
            '& .ops-mascot-hover': { display: 'inline-flex' },
          },
          '&:focus, &:focus-visible': { outline: 'none' },
          [kbFocus()]: { outline: `2px solid ${onHeader.ink}`, outlineOffset: 2 },
        }}
      >
        <OpsMascotIcon
          variant="outline"
          size={ICON_SIZE}
          playKey={tapKey}
          animation="tap"
          className="ops-mascot-rest"
        />
        <OpsMascotIcon
          size={ICON_SIZE}
          playKey={tapKey}
          animation="tap"
          color={MASCOT_HOVER}
          className="ops-mascot-hover"
        />
        {showBusy ? (
          <>
            <Box
              component="span"
              aria-hidden
              data-octo="launcher-busy"
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: onHeader.marker,
                pointerEvents: 'none',
              }}
            />
            <Box component="span" sx={octoVisuallyHidden}>
              , respondendo
            </Box>
          </>
        ) : null}
      </IconButton>
    </Tooltip>
  );
};

export default OctoplusLauncher;
